/**
 * useAdminScope — persists selectedWorksiteId and selectedTenantId in localStorage
 * per user, so filter selections survive navigation across admin pages.
 *
 * Storage keys (per user):
 *   adminScope_{userId}_ws      — selected worksite id (string | "")
 *   adminScope_{userId}_tenant  — selected tenant id  (string | "")
 *
 * Rules encoded in setTenantId:
 *   Changing the tenant resets the worksite to "" automatically.
 */
import { useState, useCallback } from "react";

function lsKey(userId, type) {
  return `adminScope_${userId || "anon"}_${type}`;
}

function lsRead(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function lsWrite(key, val) {
  try {
    localStorage.setItem(key, val || "");
  } catch {
    // localStorage unavailable (SSR, private mode with storage blocked, etc.)
  }
}

/**
 * @param {string|number} userId - stable user identifier (e.g. user.id or user.email)
 */
export function useAdminScope(userId) {
  const [worksiteId, _setWorksiteId] = useState(() =>
    lsRead(lsKey(userId, "ws"))
  );
  const [tenantId, _setTenantId] = useState(() =>
    lsRead(lsKey(userId, "tenant"))
  );

  /** Set and persist selected worksite. Pass "" to select All. */
  const setWorksiteId = useCallback(
    (val) => {
      const v = val ?? "";
      _setWorksiteId(v);
      lsWrite(lsKey(userId, "ws"), v);
    },
    [userId]
  );

  /**
   * Set and persist selected tenant.
   * Also resets the worksite to "" (different tenant = stale worksite).
   */
  const setTenantId = useCallback(
    (val) => {
      const v = val ?? "";
      _setTenantId(v);
      // Reset worksite whenever tenant changes
      _setWorksiteId("");
      lsWrite(lsKey(userId, "tenant"), v);
      lsWrite(lsKey(userId, "ws"), "");
    },
    [userId]
  );

  return { worksiteId, setWorksiteId, tenantId, setTenantId };
}
